import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
} from "react";
import {
  appApi,
  AppEpisode,
  Catalogue,
  Language,
  Localization,
  Theme,
  User,
  Note,
  Preferences,
} from "./api";
import { layouts, readAppearance } from "./preferences";
import { translator } from "./i18n";
const EMPTY: Catalogue = { episodes: [], shows: [], stations: [], artists: [] };
function stored(key: string, fallback: string) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}
export function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Browser storage is optional. */
  }
}
function useAppState() {
  const [catalogue, setCatalogue] = useState<Catalogue>(EMPTY),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [user, setUser] = useState<User | null>(null),
    [authReady, setAuthReady] = useState(false);
  const [lang, setLang] = useState<Language>(
    ["en", "fr", "ar", "ja", "original"].includes(stored("ew-lang", "en"))
      ? (stored("ew-lang", "en") as Language)
      : "en",
  );
  const [theme, setTheme] = useState<Theme>(
    (layouts as readonly string[]).includes(stored("ew-theme", "gallery"))
      ? (stored("ew-theme", "gallery") as Theme)
      : "gallery",
  );
  const [appearance, setAppearance] = useState(() => {
    try {
      return readAppearance(JSON.parse(stored("ew-appearance", "{}")));
    } catch {
      return readAppearance({});
    }
  });
  const preferenceQueue = useRef(Promise.resolve());
  useEffect(() => {
    const el = document.documentElement;
    el.style.fontSize = `${appearance.textSize}px`;
    for (const key of [
      "font",
      "palette",
      "motion",
      "contrast",
      "spacing",
      "underline",
    ] as const)
      el.dataset[key] = String(appearance[key]);
    persist("ew-appearance", JSON.stringify(appearance));
  }, [appearance]);
  const [localization, setLocalization] = useState<Localization>({
      status: "idle",
      episodes: {},
    }),
    [notes, setNotes] = useState<Note[]>([]);
  const [toast, setToast] = useState("");
  const t = translator(lang);
  function refresh() {
    setLoading(true);
    setError("");
    appApi
      .catalogue()
      .then(setCatalogue)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);
  useEffect(() => {
    appApi
      .me()
      .then((u) => {
        setUser(u);
        setLang(u.language);
        setTheme(u.theme);
        setAppearance(readAppearance(u.appearance));
      })
      .catch(() => {})
      .finally(() => setAuthReady(true));
  }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    persist("ew-theme", theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.lang = lang === "original" ? "en" : lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    persist("ew-lang", lang);
  }, [lang]);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(id);
  }, [toast]);
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    setLocalization({ status: "idle", episodes: {} });
    if (lang === "original") return;
    const poll = (start = false) =>
      appApi
        .localization(lang, start)
        .then((data) => {
          if (!active) return;
          setLocalization(data);
          if (data.status === "running") timer = setTimeout(() => poll(), 4000);
        })
        .catch((e) => {
          if (active)
            setLocalization({
              status: "unavailable",
              episodes: {},
              error: e.message,
            });
        });
    poll(Boolean(user));
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [lang, user?.id]);
  useEffect(() => {
    let active = true;
    setNotes([]);
    if (user)
      appApi
        .notes()
        .then((n) => {
          if (active) setNotes(n);
        })
        .catch((e) => {
          if (active) setToast(e.message);
        });
    return () => {
      active = false;
    };
  }, [user?.id]);
  function preference(values: Preferences) {
    if (values.language) setLang(values.language);
    if (values.theme) setTheme(values.theme);
    if (values.appearance)
      setAppearance((a) => readAppearance({ ...a, ...values.appearance }));
    if (!user) return Promise.resolve();
    preferenceQueue.current = preferenceQueue.current.then(async () => {
      try {
        const u = await appApi.preferences(values);
        setUser(u);
      } catch (e) {
        setToast(t("preferenceError") + " " + (e as Error).message);
      }
    });
    return preferenceQueue.current;
  }
  async function login(mode: string, username: string, password: string) {
    const u = await appApi.auth(mode, username, password);
    setUser(u);
    setLang(u.language);
    setTheme(u.theme);
    setAppearance(readAppearance(u.appearance));
  }
  async function logout() {
    try {
      await appApi.logout();
      setUser(null);
      setNotes([]);
    } catch (e) {
      setToast((e as Error).message);
    }
  }
  async function addNote(recording: string, ms: number, text: string) {
    const note = await appApi.addNote(recording, Math.round(ms), text);
    setNotes((n) => [note, ...n]);
    setToast(t("noteSaved"));
  }
  async function editNote(id: string, text: string) {
    const note = await appApi.editNote(id, text);
    setNotes((n) => n.map((x) => (x.id === id ? note : x)));
  }
  async function deleteNote(id: string) {
    await appApi.deleteNote(id);
    setNotes((n) => n.filter((x) => x.id !== id));
  }
  function title(ep: AppEpisode) {
    return (
      (lang === "en" ? ep.metadata.title_en : null) ||
      localization.episodes[ep.id]?.title ||
      ep.title
    );
  }
  function description(ep: AppEpisode) {
    return localization.episodes[ep.id]?.description || ep.description || "";
  }
  return {
    catalogue,
    loading,
    error,
    refresh,
    user,
    authReady,
    lang,
    theme,
    appearance,
    t,
    preference,
    login,
    logout,
    localization,
    title,
    description,
    notes,
    addNote,
    editNote,
    deleteNote,
    toast,
    notify: setToast,
  };
}
const Context = createContext<ReturnType<typeof useAppState> | null>(null);
export function AppProvider({ children }: { children: ReactNode }) {
  return <Context.Provider value={useAppState()}>{children}</Context.Provider>;
}
export function useApp() {
  const c = useContext(Context);
  if (!c) throw Error("AppProvider missing");
  return c;
}
